import { DialogComponent, DialogOpenEvent } from "@theme/dialog";
import { CartAddEvent, ThemeEvents } from "@theme/events";

/**
 * A custom element that manages a cart drawer.
 *
 * The drawer's <dialog> lives inside the header section, but cart updates only
 * morph <cart-items-component>. Dialog-level state that depends on the cart
 * (empty class, aria-labelledby, sticky summary) is synced here instead.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#syncEmptyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#syncEmptyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = (event) => {
    if (this.hasAttribute("auto-open") && !event.detail?.data?.goingToCheckout) {
      this.showDialog();
    }
  };

  /**
   * Keeps dialog-level attributes in sync with the cart, since the dialog
   * itself is no longer re-rendered on cart updates.
   * @param {CustomEvent} event
   */
  #syncEmptyState = (event) => {
    const count = event.detail?.data?.itemCount;
    if (typeof count !== "number") return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const empty = count === 0;
    dialog.classList.toggle("cart-drawer--empty", empty);
    dialog.setAttribute(
      "aria-labelledby",
      empty ? "cart-drawer-heading-empty" : "cart-drawer-heading",
    );

    // CartUpdateEvent fires before cart-items-component morphs; wait a frame
    requestAnimationFrame(() => this.#updateStickyState());
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector("shopify-payment-terms")?.shadowRoot;
      const cta = installmentsContent?.querySelector("#shopify-installments-cta");
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector(".cart-drawer__content");
    const summary = dialog.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute("cart-summary-sticky", "false");
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    if (!drawerHeight) return; // closed / not laid out yet

    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute("cart-summary-sticky", ratio > this.#summaryThreshold ? "false" : "true");
  };
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
